import { sand, violet } from "@radix-ui/colors";
import { range } from "lodash-es";
import React from "react";
import { Flex, Icon, Text, useTheme, type IconName } from "@repo/ui";
import { Badge } from "./Badges";
import CircleLogo from "../assets/logos/LogoCircle.svg?react";

const DrChoudharyImage = new URL(
	"../assets/media/DrChoudhary.png?width=100",
	import.meta.url,
);

type PolarCoordinateGridProps = {
	initialR: number;
	rStep: number;
	circleCount: number;
	color: string;
};

export const PolarCoordinateGrid: React.FC<PolarCoordinateGridProps> = ({
	initialR,
	rStep,
	circleCount,
	color,
}) => {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="100%"
			height="100%"
			viewBox="-350 -185 700 370"
		>
			<defs>
				<radialGradient id="lineFade" r="60%">
					<stop offset="0%" style={{ stopColor: color, stopOpacity: 1 }} />
					<stop offset="100%" style={{ stopColor: color, stopOpacity: 0 }} />
				</radialGradient>
				<radialGradient id="circleFade" r="100%" fr="45%">
					<stop offset="0%" style={{ stopColor: color, stopOpacity: 0 }} />
					<stop offset="100%" style={{ stopColor: color, stopOpacity: 1 }} />
				</radialGradient>
			</defs>

			{range(circleCount).map((i) => {
				return (
					<circle
						key={i}
						cx={0}
						cy={0}
						r={initialR + rStep * i}
						fill="url(#circleFade)"
						stroke={color}
						strokeWidth={1}
						strokeOpacity={(circleCount - i) / circleCount}
					/>
				);
			})}

			<line
				x1="-247.49"
				y1="-247.49"
				x2="247.49"
				y2="247.49"
				stroke="url(#lineFade)"
				strokeWidth="1"
			/>
			<line
				x1="247.49"
				y1="-247.49"
				x2="-247.49"
				y2="247.49"
				stroke="url(#lineFade)"
				strokeWidth="1"
			/>
		</svg>
	);
};

export const ProviderCard: React.FC = () => {
	const theme = useTheme();
	return (
		<Flex
			gap="xs"
			align="center"
			paddingVertical="xs"
			paddingHorizontal="xs"
			borderRadius={"lg"}
			borderWidth={"lg"}
			borderColor="accent9"
			style={{
				minWidth: 180,
				background: `linear-gradient(${theme.colors.accentA7}, transparent), ${sand.sand1}`,
			}}
		>
			<Flex
				align="center"
				width="xxxl"
				height="xxxl"
				shrink={0}
				borderRadius={"full"}
				borderWidth={"lg"}
				borderColor="sand1"
				style={{ overflow: "hidden" }}
			>
				<img
					src={DrChoudharyImage.toString()}
					style={{ width: "100%", height: "100%" }}
				/>
			</Flex>
			<Flex column grow shrink gap="xxxs" style={{ minWidth: 0 }}>
				<Text size="md" weight={"semibold"} color="sand12">
					Dr. Choudhary
				</Text>
				<Flex column grow shrink>
					<Text
						truncate
						size="sm"
						color="sand11"
						style={{ minWidth: 0, flex: 1 }}
					>
						<Icon
							name="Star"
							color="sand12"
							size="xs"
							fill="sand12"
							style={{ transform: `translateY(1px)` }}
						/>
						<Text size="sm" weight={"medium"} color="sand12">
							{" "}
							Best match{" "}
						</Text>
						— Has 10 years of documented experience treating patients with
						chronic mu...
					</Text>
				</Flex>
			</Flex>
		</Flex>
	);
};

type InfoSourceBadge = {
	icon: IconName;
	text: string;
	top: number;
	left: number;
};

const badges: InfoSourceBadge[] = [
	{
		icon: "FileUser",
		text: "Personal health history",
		top: 24.5,
		left: 20,
	},
	{
		icon: "Hospital",
		text: "EHR systems",
		top: 14.4,
		left: 50.2,
	},
	{
		icon: "BookMarked",
		text: "Provider directories",
		top: 26,
		left: 80,
	},
	{
		icon: "TestTube",
		text: "Clinical research",
		top: 44.7,
		left: 85.9,
	},
	{
		icon: "Globe",
		text: "Online reviews and forums",
		top: 62.5,
		left: 82,
	},
	{
		icon: "Capital",
		text: "Government programs",
		top: 60.8,
		left: 20.3,
	},
	{
		icon: "FileText",
		text: "Insurance claims",
		top: 42.9,
		left: 14,
	},
];

export const RightInformationDiagram: React.FC = () => {
	const theme = useTheme();

	return (
		<Flex column align="center" style={{ width: `clamp(320px,100%,640px)` }}>
			<Flex
				wrap
				justify="center"
				gap="xs"
				style={{ display: "none", marginBottom: -60, zIndex: 5 }}
				tablet={{ style: { display: "flex" } }}
			>
				{badges.map((badge, index) => (
					<Badge key={index} icon={badge.icon} text={badge.text} />
				))}
			</Flex>
			<div
				style={{
					position: "relative",
					width: "100%",
					aspectRatio: "16 / 11",
					overflow: "hidden",
				}}
			>
				<div style={{ position: "absolute", inset: 0 }}>
					<PolarCoordinateGrid
						initialR={50}
						rStep={50}
						circleCount={4}
						color={sand.sand6}
					/>
					<svg
						width="100%"
						height="100%"
						viewBox="0 0 640 440"
						fill="none"
						style={{ position: "absolute", inset: 0 }}
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M320.285 117.742V178.407"
							stroke={violet.violet9}
							strokeWidth={1.5}
						/>
						<path
							d="M399.868 166.014L358.119 195.368"
							stroke={violet.violet6}
							strokeWidth={1.5}
						/>
						<path
							d="M409 222.112H385.517"
							stroke={violet.violet9}
							strokeWidth={1.5}
						/>
						<path
							d="M273.318 158.185L285.06 175.145"
							stroke={violet.violet6}
							strokeWidth={1.5}
						/>
						<path
							d="M212 177.755L278.536 205.805"
							stroke={violet.violet9}
							strokeWidth={1.5}
						/>
						<path
							d="M285.06 291.258L298.106 261.252"
							stroke={violet.violet6}
							strokeWidth={1.5}
						/>
						<path
							d="M235.484 261.251L273.318 241.682"
							stroke={violet.violet9}
							strokeWidth={1.5}
						/>
						<path
							d="M350.291 239.073L393.344 270.384"
							stroke={violet.violet6}
							strokeWidth={1.5}
						/>
					</svg>
				</div>
				<div
					style={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: `translate(-50%, -50%)`,
					}}
				>
					<CircleLogo
						width={48}
						height={48}
						style={
							{
								"--background": theme.colors.accent9,
								"--foreground": theme.colors.sand1,
							} as React.CSSProperties
						}
					/>
				</div>
				<div
					style={{
						position: "absolute",
						width: 2,
						height: "25%",
						backgroundColor: theme.colors.accent9,
						top: "calc(50% + 20px)",
						left: "50%",
						transform: `translate(-50%, 0)`,
					}}
				/>
				<Flex
					paddingHorizontal="md"
					style={{
						position: "absolute",
						width: `min(400px, 100%)`,

						top: "81%",
						left: "50%",
						transform: `translate(-50%, -50%)`,
					}}
				>
					<ProviderCard />
				</Flex>

				{badges.map((badge, index) => (
					<Flex
						key={index}
						style={{
							position: "absolute",
							transform: `translate(-50%, -50%)`,
							top: `${badge.top}%`,
							left: `${badge.left}%`,
						}}
						tablet={{ style: { display: "none" } }}
					>
						<Badge icon={badge.icon} text={badge.text} />
					</Flex>
				))}
			</div>
		</Flex>
	);
};
